import React, { useState, useEffect } from 'react';
import { Lock, Unlock, CheckCircle } from 'lucide-react';

/**
 * BankVault - Animated bank vault component
 * Shows a metallic vault with 12 locks (one per month) and a progress dial
 * Unlocks progressively as the player earns characters
 */
const BankVault = ({ 
  totalMonths = 12, 
  unlockedMonths = 0, 
  totalCharacters = 24,
  earnedCharacters = 0,
  isFullyUnlocked = false,
  size = 'medium', // 'small', 'medium', 'large'
  showLabels = true,
  animate = true 
}) => {
  const [dialRotation, setDialRotation] = useState(0);

  // Calculate progress percentage
  const progressPercent = Math.round((earnedCharacters / totalCharacters) * 100);
  
  // Dial rotation based on progress (0% = 0°, 100% = 360°)
  const targetRotation = (progressPercent / 100) * 360;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDialRotation(targetRotation);
    }, animate ? 100 : 0);
    return () => clearTimeout(timer);
  }, [targetRotation, animate]);

  // Size configurations
  const sizeConfig = {
    small: {
      vault: 'w-32 h-32',
      dial: 'w-16 h-16',
      lock: 'w-3 h-3',
      text: 'text-xs',
      lockRing: 52,
    },
    medium: {
      vault: 'w-48 h-48 md:w-56 md:h-56',
      dial: 'w-20 h-20 md:w-24 md:h-24',
      lock: 'w-3 h-3 md:w-4 md:h-4',
      text: 'text-xs md:text-sm',
      lockRing: 70,
    },
    large: {
      vault: 'w-64 h-64 md:w-72 md:h-72',
      dial: 'w-28 h-28 md:w-32 md:h-32',
      lock: 'w-4 h-4 md:w-5 md:h-5',
      text: 'text-sm md:text-base',
      lockRing: 90,
    },
  };

  const config = sizeConfig[size];

  // Generate lock positions in a circle
  const getLockPosition = (index, total, radius) => {
    const angle = (index / total) * 2 * Math.PI - Math.PI / 2; // Start from top
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    return { x, y, angle: (angle * 180) / Math.PI + 90 };
  };

  const monthLabels = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

  return (
    <div className="relative flex flex-col items-center">
      {/* Main Vault Container */}
      <div className={`relative ${config.vault} rounded-full bg-gradient-to-br from-slate-600 via-slate-500 to-slate-700 shadow-2xl border-4 border-slate-400`}>
        
        {/* Metallic texture overlay */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-white/10 via-transparent to-black/20" />
        
        {/* Inner ring */}
        <div className="absolute inset-3 rounded-full bg-gradient-to-br from-slate-700 via-slate-600 to-slate-800 border-2 border-slate-500">
          
          {/* 12 Locks around the dial */}
          {Array.from({ length: totalMonths }).map((_, index) => {
            const pos = getLockPosition(index, totalMonths, config.lockRing);
            const isUnlocked = index < unlockedMonths;
            
            return (
              <div
                key={index}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500"
                style={{
                  left: `calc(50% + ${pos.x}px)`,
                  top: `calc(50% + ${pos.y}px)`,
                }}
              >
                {/* Lock container */}
                <div 
                  className={`relative flex items-center justify-center rounded-full transition-all duration-500 ${
                    isUnlocked 
                      ? 'bg-gradient-to-br from-green-400 to-emerald-600 shadow-lg shadow-green-500/50' 
                      : 'bg-gradient-to-br from-amber-600 to-amber-800 shadow-md'
                  }`}
                  style={{ 
                    width: size === 'small' ? '20px' : size === 'medium' ? '26px' : '32px',
                    height: size === 'small' ? '20px' : size === 'medium' ? '26px' : '32px',
                  }}
                >
                  {isUnlocked ? (
                    <CheckCircle className={`${config.lock} text-white`} />
                  ) : (
                    <Lock className={`${config.lock} text-amber-200`} />
                  )}
                  
                  {/* Month label */}
                  {showLabels && (
                    <span 
                      className={`absolute ${config.text} font-bold ${isUnlocked ? 'text-green-300' : 'text-amber-300'}`}
                      style={{
                        top: size === 'small' ? '-14px' : size === 'medium' ? '-18px' : '-22px',
                      }}
                    >
                      {monthLabels[index]}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          
          {/* Center Dial */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`${config.dial} rounded-full bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 border-4 border-slate-500 shadow-inner flex items-center justify-center relative overflow-hidden`}>
              
              {/* Dial notches */}
              {Array.from({ length: 12 }).map((_, i) => {
                const angle = (i / 12) * 360;
                return (
                  <div
                    key={i}
                    className="absolute w-0.5 h-2 bg-slate-400"
                    style={{
                      transform: `rotate(${angle}deg) translateY(-${size === 'small' ? '24' : size === 'medium' ? '36' : '48'}px)`,
                    }}
                  />
                );
              })}
              
              {/* Rotating dial indicator */}
              <div 
                className="absolute w-1 h-1/2 origin-bottom transition-transform duration-1000 ease-out"
                style={{ 
                  transform: `rotate(${dialRotation}deg)`,
                  bottom: '50%',
                }}
              >
                <div className="w-full h-full bg-gradient-to-t from-red-600 to-red-400 rounded-t-full" />
              </div>
              
              {/* Center cap */}
              <div className={`absolute ${size === 'small' ? 'w-6 h-6' : size === 'medium' ? 'w-10 h-10 md:w-12 md:h-12' : 'w-14 h-14'} rounded-full bg-gradient-to-br from-slate-600 to-slate-800 border-2 border-slate-500 flex items-center justify-center`}>
                {isFullyUnlocked ? (
                  <Unlock className={`${size === 'small' ? 'w-3 h-3' : size === 'medium' ? 'w-4 h-4 md:w-5 md:h-5' : 'w-6 h-6'} text-green-400`} />
                ) : (
                  <span className={`${config.text} font-bold text-amber-400`}>
                    {progressPercent}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Vault handle (decorative) */}
        <div className="absolute -right-2 top-1/2 transform -translate-y-1/2">
          <div className={`${size === 'small' ? 'w-4 h-8' : size === 'medium' ? 'w-5 h-10 md:w-6 md:h-12' : 'w-8 h-16'} bg-gradient-to-r from-slate-500 to-slate-600 rounded-r-lg border-2 border-slate-400`} />
        </div>
        
        {/* Glow effect when fully unlocked */}
        {isFullyUnlocked && (
          <div className="absolute inset-0 rounded-full animate-pulse">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-green-400/30 to-emerald-500/30 blur-xl" />
          </div>
        )}
      </div>
      
      {/* Progress text below */}
      {showLabels && (
        <div className="mt-4 text-center">
          <p className={`${config.text} font-bold ${isFullyUnlocked ? 'text-green-400' : 'text-amber-400'}`}>
            {isFullyUnlocked ? '🔓 COFFRE PRÊT !' : `${earnedCharacters}/${totalCharacters} caractères`}
          </p>
          <p className={`${size === 'small' ? 'text-[10px]' : 'text-xs'} text-slate-400 mt-1`}>
            {unlockedMonths}/{totalMonths} mois déverrouillés
          </p>
        </div>
      )}
    </div>
  );
};

export default BankVault;
